import { useMemo } from 'react';
import { trpcPricingListSkusStartingAtPriceOpen } from '@trashlab/api/src/generated/hooks';
import { useVariantValue } from '@trashlab/sitesapp/src/organizations/piedmont/lib/utils/get-variant-value';

export const useFilteredSkus = (
  tenantId: string,
  selectedType: string = '',
) => {
  const skusQuery = trpcPricingListSkusStartingAtPriceOpen.useQuery({
    tenantId: tenantId ?? '',
    onlineOrderEnabled: true,
    // Exclude custom price cards (customer-specific or site-specific cards)
    defaultOnly: true,
  });

  const residentialDescription = useVariantValue(
    'RESIDENTIAL_SERVICE_DESCRIPTION',
    'Hassle-free residential dumpsters delivered straight to your driveway',
  );

  const commercialDescription = useVariantValue(
    'COMMERCIAL_SERVICE_DESCRIPTION',
    'Seamless commercial dumpsters delivered directly to your business',
  );

  const rolloffDescription = useVariantValue(
    'ROLLOFF_SERVICE_DESCRIPTION',
    'Effortless rolloff dumpsters delivered straight to your job site fast',
  );

  const portableToiletDescription = useVariantValue(
    'PORTABLE_TOILET_SERVICE_DESCRIPTION',
    'Clean, reliable portable toilets for events and worksites',
  );

  const residentialLabel = useVariantValue(
    'RESIDENTIAL_SERVICE_LABEL',
    'Residential Service',
  );

  const commercialLabel = useVariantValue(
    'COMMERCIAL_SERVICE_LABEL',
    'Front Load Service',
  );

  const rolloffLabel = useVariantValue(
    'ROLLOFF_SERVICE_LABEL',
    'Rolloff Dumpsters',
  );

  const portableToiletLabel = useVariantValue(
    'PORTABLE_TOILET_SERVICE_LABEL',
    'Portable Toilets',
  );

  const getTypeDescription = (typeValue: string) => {
    switch (typeValue) {
      case 'residential':
        return residentialDescription;
      case 'commercial':
        return commercialDescription;
      case 'rolloff':
        return rolloffDescription;
      case 'portableToilet':
        return portableToiletDescription;
      default:
        return '';
    }
  };

  const { filteredSkus, getSkusByType, skuOptions, containerTypeOptions } =
    useMemo(() => {
      const filterSkusByType = (type: string) => {
        if (!skusQuery.data) return [];

        return skusQuery.data.filter((sku) => {
          switch (type) {
            case '':
              return true;
            case 'residential':
              return sku.product?.containerType === 'CART';
            case 'commercial':
              return sku.product?.containerType === 'FRONT_LOAD';
            case 'portableToilet':
              return sku.product?.containerType === 'LIQUID_WASTE_CONTAINER';
            case 'rolloff':
              return sku.product?.containerType === 'DUMPSTER';
            default:
              return false;
          }
        });
      };

      const options =
        skusQuery.data?.map((sku) => ({
          value: sku.id,
          label: sku.customerFacingName || sku.name || 'Unnamed Service',
        })) || [];

      const containerTypes = [
        {
          value: 'residential',
          label: residentialLabel,
          href: { to: '/dumpster-rentals', search: { type: 'residential' } },
        },
        {
          value: 'commercial',
          label: commercialLabel,
          href: { to: '/dumpster-rentals', search: { type: 'commercial' } },
        },
        {
          value: 'rolloff',
          label: rolloffLabel,
          href: { to: '/dumpster-rentals', search: { type: 'rolloff' } },
        },
        {
          value: 'portableToilet',
          label: portableToiletLabel,
          href: { to: '/dumpster-rentals', search: { type: 'portableToilet' } },
        },
      ].filter((type) => {
        return filterSkusByType(type.value).length > 0;
      });

      return {
        filteredSkus: filterSkusByType(selectedType),
        getSkusByType: (type: string) => filterSkusByType(type).length,
        skuOptions: options,
        containerTypeOptions: containerTypes,
      };
    }, [
      skusQuery.data,
      selectedType,
      residentialLabel,
      commercialLabel,
      rolloffLabel,
      portableToiletLabel,
    ]);

  return {
    filteredSkus,
    getSkusByType,
    skuOptions,
    containerTypeOptions,
    isLoading: skusQuery.isLoading,
    isError: skusQuery.isError,
    getTypeDescription,
  };
}; 